// English translations — mirrors the structure of it.ts.
import type { Resources } from "./it.js";

export const en: Resources = {
  help: {
    button: "Help & guides",
    title: "Guide center",
    subtitle: "Choose what you want to learn. The guide highlights the parts of the screen step by step.",
    forThisPage: "Guide for this page",
    general: "General",
    start: "Start guide",
    next: "Next",
    prev: "Back",
    done: "Done",
    navTitle: "Navigation",
    navText: "From here you open the side menu and move between sections: Members, Services, Payments, Statistics, WhatsApp…",
    headerTitle: "Section header",
    headerText: "Shows where you are. The main page actions are on the right.",
    refreshTitle: "Refresh",
    refreshText: "Reloads the page data with the latest changes.",
    createTitle: "Create new",
    createText: "Opens the form to add a new item in this section.",
    rowMenuTitle: "Row actions",
    rowMenuText: "The ⋮ menu on each row opens the available operations: edit, and where applicable disable and delete.",
    helpTitle: "Guide center",
    helpText: "You can always find the guides here: press this button anytime.",
    overviewTitle: "App overview",
    overviewDesc: "How to move between sections and recognise the elements common to every page.",
    detailsTitle: "How it works",
    searchTitle: "Search and filters",
    debitsFiltersTitle: "Filter by date",
    debitsFiltersText: "Three modes: Period = a single month (MM/YYYY); Year = all months of a year; Custom = a from–to date range on the payment day. The view always starts from the current period.",
    debitsSocioTitle: "Filter by member",
    debitsSocioText: "Pick a member to see only their payments; leave empty to see all. The search field also filters by receipt number.",
    sec: {
      dashboard: { title: "The Dashboard", intro: "The home summarises the period's key figures (active members, credits, collected, balance) and gives quick access to sections.", details: "Top-right you choose the period: all cards recompute. The shortcuts at the bottom open Members, Services, Credits, Receipts, Costs, WhatsApp, Payments and Statistics directly. The progress bar shows collected vs total due for the period." },
      partners: { title: "Manage Members", intro: "Here you manage the members registry: create, edit, disable or delete a member.", details: "'New member' opens the form: personal data (first/last name, tax code), one or more contacts (phone, email) and the member type (Owner, Horse, Pony, Other). From each row's ⋮: Edit fixes the data; Disable removes the member from operational selections while keeping their history (reversible); Delete removes them permanently (irreversible). At the top you search by name/tax code and filter by status (All/Active/Inactive) and member type." },
      suppliers: { title: "Manage Suppliers", intro: "Suppliers registry: create, edit, disable or delete a supplier.", details: "The form collects company and/or person name, contacts and useful data. From the ⋮: Edit, Disable (hides from selections keeping history) and Delete. Suppliers are then referenced in Costs." },
      services: { title: "Services Catalogue", intro: "The catalogue of courses/services: create, set price and billing, edit or delete.", details: "Creating a service you set name, type, price and renewal frequency. If frequency is Monthly you pick the 'billing months' (click the x on a month to remove it). You set whether it's tax-deductible and whether it sends notifications. These services feed member subscriptions and credit generation." },
      acqservices: { title: "Acquired Services", intro: "The catalogue of supplier cost items, later used in Costs.", details: "Each item has name, description, Cost/Service type and Economic report item (both dropdowns with readable labels), price and email notification. When you register a Cost, picking the item pre-fills these fields." },
      partnerServices: { title: "Member Services", intro: "Assign recurring services to members, with per-member overrides.", details: "Pick member and service to create the link; you can override name, description, notifications and deductibility for that member. Filter by member or service. These links are the basis for generating monthly credits." },
      creditsGen: { title: "Monthly preparation", intro: "Generate the period credits and assign services to members.", details: "Pick a service template: fields (name, price, description, notifications, deductible) pre-fill and stay editable. The + next to a member adds that credit to their sheet for the selected period; alternatively 'Calculate' auto-generates the period's credits from active subscriptions. On the right, the period's credits (newest first): from the ⋮ open the member's receipt or delete a credit." },
      credits: { title: "Credits", intro: "The list of what members owe, per period.", details: "Filter by period (or 'All') and search by member/service/description. Each row shows member, period, amount and whether it's deductible. Sorted newest first. The filtered total is top-right. Credits are the amount due; payments (Debits) offset them." },
      debits: { title: "Payments", intro: "Payments received from members: register a payment and browse the filterable history.", details: "With 'Register payment' you pick the member, amount, date, method (Cash, Bank transfer, Credit card, Debit card, Electronic payment) and the accounting period (MM/YYYY). Reconciliation: a payment can't exceed the period's remaining credit; when total paid equals the credit the sheet becomes 'settled' and the settle date is recorded. The list is sorted by most recent payment date; the filtered total is at the top." },
      receipts: { title: "Receipts", intro: "Payment sheets per member and period.", details: "Filter by year, status (All / Unpaid / Settled), active/inactive members and member type. From each receipt's ⋮: Open the sheet, Print, Export PDF, Send via WhatsApp, go to the member's Credits or Payments, Delete. Opening the sheet you register payments (with method and date) and see credits/items: reconciliation auto-updates Due, Paid, To pay and the status. 'Unpaid' shows all open sheets from any year." },
      costs: { title: "Costs", intro: "The stable's outgoings to suppliers.", details: "With 'New cost' you pick Supplier and Acquired service (name, description, price and type pre-fill), then amount, payment date, method and whether it's settled. Filter by year and export the summary PDF by type. From the ⋮: Edit or Delete." },
      summary: { title: "Financial Summary", intro: "Period dashboard and per-member detail.", details: "Dashboard tab: total credits, collected, costs, net balance, collection rate and number of settled/unsettled sheets. Per-member detail tab: each member expands to show items (course and extras); you can expand/collapse all, print and export the PDF, and the list is paginated. Change the period top-right." },
      statistics: { title: "Statistics", intro: "Period analysis across four views.", details: "Members tab: summary by type (courses, extras, collected) and item detail. Services tab: amounts by service type. Unpaid tab: unsettled sheets by period, with active/inactive filter and scope (period only or period + previous). Summary tab: per single member. At the top filter by member type and period; you can print and export the PDF. Long lists are paginated." },
      waCompose: { title: "Send WhatsApp messages", intro: "Prepare and queue a bulk send to members.", details: "Pick the template message; if it expects a generic attachment, upload the file (PDF or image). Filter members by type and status and search by name; move them from 'Available' to 'To send' with the + (or 'Add all'). Members without a valid number can't be selected. On queueing, one queue item is created per valid mobile number; the actual send then runs from Queues." },
      waQueues: { title: "WhatsApp Queues", intro: "The items ready to send.", details: "'Launch send JOB' processes the queue via WhatsApp respecting the 24h window. From each item's ⋮: 'Re-queue' (back to pending and restart the send) or 'Delete'. The status (pending, sent, failed…) updates in real time. The list is paginated." },
      waTemplates: { title: "WhatsApp Templates", intro: "Reusable message templates.", details: "With 'New template' you set name, message text and attachment type: None, Last receipt, Unpaid receipts or Generic attachment (file uploaded at send time). From the ⋮: Edit or Delete. Templates are then chosen in 'Send messages'." },
      waMessages: { title: "WhatsApp Messages", intro: "History of sends, grouped by recipient.", details: "Each group gathers the messages sent to a member with status (sent, delivered, read, failed) and date. Long texts are truncated: 'Read all' opens the modal with the full message and downloadable attachments. 'Resend' relaunches the whole send to the member: if the 24h window is closed the request message goes first. Filter by status and search by number or text." }
    },
    formTitle: "Filling in the form",
    formNote: "This guide is illustrative only: it does not save and performs no operation.",
    partnerForm: {
      active: { title: "Active member", desc: "Whether the member is operational. When off they stay archived but don't appear in selections (credits, sends…). Reversible anytime." },
      type: { title: "Member type", desc: "Member classification: Owner, Horse, Pony or Other. Drives groupings, filters and statistics." },
      name: { title: "Personal data", desc: "First and last name (required). Below: birth country and town, date of birth and tax code. If the birth date indicates a minor, the Legal representative and Deductibility sections appear automatically." },
      residence: { title: "Residence", desc: "Town of residence (auto-fills the postcode), street type (Street, Square…), street name and number." },
      document: { title: "Document", desc: "Document type (ID card, Passport…), identifier, expiry date and issuing authority." },
      email: { title: "Email", desc: "One or more emails with description and type (Personal/Office). '+ Add email' inserts more, the bin removes them. The first is the main one." },
      contacts: { title: "Contacts", desc: "One or more numbers with prefix, description and type (Mobile/Home/Office). '+ Add contact' adds more; the first is the main one, used for WhatsApp sends." },
      save: { title: "Save or cancel", desc: "'Save' records the member; 'Cancel' closes without saving. No operation is performed during the guide." }
    },
    forms: {
      suppliers: "Fill in the supplier data: company and/or person name, tax code/VAT, contacts (phone, email) and address. The 'Active' switch makes them operational; when off they stay archived. Suppliers are then used in Costs. 'Save' records, 'Cancel' closes without changes.",
      services: "Define the service/course: Name, Type, Price and Renewal frequency. If frequency is Monthly the 'billing months' appear (click the x on a month to remove it). The switches set whether the service is Deductible and whether it sends Notifications. 'Save' records, 'Cancel' closes.",
      acqservices: "Create a supplier cost item: Name, Description, Cost/Service type and Economic report item (dropdowns with readable labels), Price and Email notification. This item will appear among the Acquired services selectable in Costs.",
      partnerServices: "Link a recurring service to a member: pick Member and Service. Below you can override for that member the Name, Description, Notifications and Deductibility from the service defaults. 'Save' creates or updates the link.",
      costs: "Register a cost: pick Supplier and Acquired service (Name, Description, Price and Type pre-fill), then Amount, Payment date, Method and the Settled switch. 'Save' records the cost, 'Cancel' closes.",
      debits: "Register a member payment: pick the Member, the Amount, the accounting Period (MM/YYYY), the Date and the Method (Cash, Bank transfer, Card…). Remember reconciliation: a payment can't exceed the period's remaining credit; at par the sheet becomes settled.",
      templates: "Create a message template: Name, Attachment type (None, Last receipt, Unpaid receipts, Generic attachment) and Message text. The template will then be selectable in 'Send messages'. 'Save'/'Create' records, 'Cancel' closes.",
      receiptPayment: "Register a payment on the sheet: Amount (pre-filled with the remaining due), Payment date and Method. At the bottom you see the Due/Paid/Remaining summary. On save, reconciliation updates the sheet status."
    },
    cg: {
      period: { title: "Working period", desc: "Pick the month (MM/YYYY) to work on: everything you generate or assign below refers to this period." },
      calc: { title: "Calculate (auto-generation)", desc: "Automatically generates the period's credits for all members from their active subscriptions (Member Services): it creates the missing entries and skips existing ones, so no duplicates. You're asked to confirm before proceeding. Use it to prepare the month's recurring fees in one go." },
      service: { title: "1) Pick a service", desc: "Select a service from the catalogue: Name, Price, Description, Notifications and Deductibility pre-fill automatically from that service's values." },
      customize: { title: "2) Customise the values", desc: "You can override the defaults for this assignment only: change Name, Price (€) and Description without altering the catalogue. Name, price (> 0) and description are required to add the credit." },
      toggles: { title: "Notifications and deductibility", desc: "Turn on 'Notifications' if the member should be notified, and 'Deductible' if the credit is tax-deductible. They start from the service values and stay editable." },
      partners: { title: "3) Pick the member", desc: "On the left, the list of active members: use search to find them quickly. Next to each is the + button to assign the service filled in above." },
      add: { title: "4) Add the credit to the member", desc: "The + next to a member adds the credit (the amount due) to their sheet for the period, with the template values. The + is enabled only after picking a service and filling name, price and description. It's a single, targeted add: no other member is touched." },
      credits: { title: "Period credits", desc: "On the right you see the credits already present for the period, newest first. The 'Only selected service' switch shows only those for the service chosen above." },
      rowMenu: { title: "Credit actions", desc: "From each credit's ⋮ menu you can open the member's receipt or delete that credit." }
    },
    rc: {
      socio: { title: "Filter by member", desc: "Pick a member to see only their sheets; leave empty to see all." },
      year: { title: "Year", desc: "Filter receipts by year. Exception: with 'Unpaid' all open sheets from any year are shown." },
      mode: { title: "Sheet status", desc: "All, Unpaid (still-open amount) or Settled. 'Unpaid' ignores the year and shows every uncollected balance: handy for reminders." },
      active: { title: "Active / inactive members", desc: "Show only active members, only inactive, or all. Inactive members are highlighted in red with 🚫." },
      type: { title: "Member type", desc: "Narrows to Owner, Horse or Pony." },
      rowMenu: { title: "Receipt actions", desc: "From the ⋮ menu: Open the sheet (register payments and see credits/items; reconciliation auto-updates Due, Paid, To pay and the status), Print, Export PDF, Send via WhatsApp, go to the member's Credits or Payments, Delete." }
    },
    cr: {
      search: { title: "Search", desc: "Search by member name, service name or description." },
      period: { title: "Period", desc: "Filter credits by a single month (MM/YYYY) or choose 'All' to see them across all periods." },
      total: { title: "Filtered total", desc: "The sum of the currently filtered credits: the total due for the current selection." },
      meaning: { title: "What credits are", desc: "A credit is what a member owes for a service in a period. It is offset by payments (Payments/Receipts): when paid equals due the sheet becomes settled. The list is sorted newest first; each row shows member, period, amount and whether it's deductible." }
    },
    st: {
      period: { title: "Period", desc: "The whole analysis refers to the month (MM/YYYY) chosen here at the top." },
      types: { title: "Filter by member type", desc: "Limit the statistics to Owner, Horse or Pony (or All). The filter applies to every tab." },
      actions: { title: "Print / Export PDF", desc: "Generate the PDF of the current view, based on the active tab and filters: 'Print' opens printing, 'Export PDF' opens the document in a new tab." },
      kpi: { title: "Summaries", desc: "The cards at the top show the totals for the active tab and change with the filters (e.g. Courses, Extras, Total, Collected)." },
      soci: { title: "Members tab", desc: "Summary by member type: count and amount of Courses and Extras, with a Total row. Below, the item detail by description." },
      servizi: { title: "Services tab", desc: "Amounts grouped by service type: cards per type at the top, the detail by description below." },
      insoluti: { title: "Unpaid tab", desc: "The unsettled sheets. Cards with total due, collected, balance and sheet count. Extra filters: active/inactive members and scope (period only or period + previous). Below, the paginated list with period, member, sheet, open amount and collected." },
      sommario: { title: "Summary tab", desc: "One row per member with main course, courses/extras, paid and balance. Paginated list, useful for the overall picture." }
    },
    wa: {
      flow: { title: "How messaging works", desc: "The flow has 4 steps: 1) TEMPLATES — create reusable message templates; 2) SEND MESSAGES — pick a template and recipients and queue them; 3) QUEUES — launch the actual WhatsApp send; 4) MESSAGES — check the outcome (delivered/read/failed) and resend if needed." },
      compose: {
        template: { title: "1) Pick the message", desc: "Select a template from those created in the Templates section. Next to it you see the attachment type expected by the template." },
        preview: { title: "Message preview", desc: "The message text in read-only: exactly what the member will receive." },
        file: { title: "Attachment (if required)", desc: "If the template expects a 'Generic attachment', here you upload the file (PDF or image) that will be attached for all selected recipients. Required to queue." },
        filters: { title: "2) Filter members", desc: "Narrow the list by member type and status (active/inactive) and search by name." },
        lists: { title: "3) Pick recipients", desc: "On the left 'Available': the + moves a member to 'To send' (or 'Add all'); the x removes them. Members without a valid phone number are greyed out and not selectable." },
        enqueue: { title: "4) Queue the send", desc: "Puts the messages in the queue: one item is created per valid mobile number of each chosen member. The actual send then runs from the Queues section." }
      },
      queues: {
        launch: { title: "Launch the send", desc: "'Launch send JOB' processes the queue and sends via WhatsApp, respecting the 24h window. Item status (pending, sent, failed…) updates in real time." },
        rowMenu: { title: "Item actions", desc: "From each item's ⋮ menu: 'Re-queue' (back to pending and restart the send) or 'Delete'." }
      },
      templates: {
        create: { title: "New template", desc: "Create a reusable template: Name, Attachment type (None, Last receipt, Unpaid receipts, Generic attachment) and Text. The '?' inside the dialog explains each field in detail." },
        rowMenu: { title: "Edit or delete", desc: "From the ⋮ menu (or the pencil/bin icons on desktop) you can edit or delete an existing template." }
      },
      messages: {
        filters: { title: "Filters", desc: "Search by number or text and filter by status (sent, delivered, read, pending, failed). On the right, the delivered and failed counters." },
        list: { title: "History by recipient", desc: "Each block groups the messages sent to a member, with status and date of each message (text and any attachments)." },
        readmore: { title: "Read all", desc: "Long texts are truncated: 'Read all' opens a dialog with the full message and downloadable attachments." },
        resend: { title: "Resend", desc: "Relaunches the whole send to that member. If the 24h window is closed, a request message goes first and the send resumes once the member replies." }
      }
    },
    rd: {
      intro: { title: "Receipt sheet", desc: "A member's payment sheet for a period: here you register payments, see the items and manage the receipt." },
      summary: { title: "Summary", desc: "Period, Due (sum of credits), Paid (sum of payments), To pay (remaining) and Status. Everything updates automatically on each payment (reconciliation): when Paid equals Due the sheet is Settled." },
      payments: { title: "Payments received", desc: "The list of payments recorded for this sheet, with date, method and amount. From the ⋮ menu you can edit or delete a payment." },
      payAdd: { title: "Register payment", desc: "Opens the dialog to enter a payment (amount, date, method). The payment can't exceed the remaining due; the '?' inside the dialog explains each field." },
      competences: { title: "Items (credits)", desc: "The period's credit items for this member (course and extras) with amount. It's the breakdown of what makes up the Due total." },
      actions: { title: "Sheet actions", desc: "At the top: Back (return to the list), Print, Export PDF and Send the receipt to the member via WhatsApp." }
    },
    user: {
      intro: { title: "User profile", desc: "Your account data (name, email, roles). Here you check which user you're signed in as. Language and logout are in the menu at the bottom of the sidebar." }
    },
    sm: {
      period: { title: "Period", desc: "All summary figures refer to the month (MM/YYYY) chosen here at the top." },
      dashboard: { title: "Dashboard", desc: "The period overview: the cards show Total credits (due), Collected, Costs and Net balance. The 'Collection rate' bar shows how much was collected of the total due. Below, the receipts detail: settled, unsettled and total sheets, with the period's net balance." },
      detail: { title: "Per-member detail", desc: "The list of members with the period's due amount. Filter by single member or member type. Click a row (or use 'Expand all') to see the member's items (course and extras). You can Print and Export the summary PDF. At the bottom the member count and total; the list is paginated." }
    }
  },
  versions: {
    title: "Versions",
    frontend: "Frontend",
    build: "Build",
    services: "Services",
    ok: "online",
    ko: "offline",
    unavailable: "Versions list unavailable"
  },
  common: {
    loading: "Loading...",
    error: "An error occurred",
    refresh: "Refresh",
    rowsPerPage: "Rows per page:",
    sessionExpired: "Session expired. You will be redirected to login in a moment…",
    newVersionAvailable: "A new version is available",
    updateNow: "Update",
    save: "Save",
    saved: "Saved",
    cancel: "Cancel",
    delete: "Delete",
    deleted: "Deleted",
    edit: "Edit",
    add: "Add",
    new: "New",
    search: "Search",
    filter: "Filter",
    active: "Active",
    inactive: "Inactive",
    activeF: "Inactive",
    settled: "Settled",
    pending: "Pending",
    all: "All",
    emptyTitle: "No items",
    emptyDescription: "There is no data to display.",
    openNavMenu: "Open navigation menu",
    toggleNavMenu: "Collapse or expand the sidebar",
    yes: "Yes",
    no: "No",
    confirm: "Confirm",
    close: "Close",
    back: "Back",
    next: "Next",
    export: "Export",
    exportPdf: "Export PDF",
    print: "Print",
    pdf: "PDF",
    open: "Open",
    actions: "Actions",
    noData: "No data",
    total: "Total",
    amount: "Amount",
    date: "Date",
    period: "Period",
    year: "Year",
    status: "Status",
    notes: "Notes",
    name: "Name",
    description: "Description",
    saving: "Saving...",
    deleteConfirm: "Are you sure you want to delete this item?",
    requiredField: "Required field",
    confirmDeleteTitle: "Confirm deletion",
    pageInProgress: "Page being implemented in v2.",
    language: "Language",
    italian: "Italian",
    english: "English"
  },

  sidebar: {
    brand: "Kappa Equestre",
    brandSubtitle: "Stable Management",
    groupRegistry: "Registry",
    groupFinance: "Finance",
    groupMembers: "Members",
    groupSuppliers: "Suppliers & costs",
    groupCommunications: "Communications",
    dashboard: "Dashboard",
    composeWa: "Send messages",
    queuesWa: "WA Queues",
    templatesWa: "WA Templates",
    messagesWa: "WA Messages",
    profile: "Profile",
    manager: "Manager"
  },

  nav: {
    home: "Home",
    partners: "Members",
    suppliers: "Suppliers",
    services: "Courses/extra",
    acquiredServices: "Acquired Services",
    partnerServices: "Periodic Fees",
    credits: "Credits",
    creditsGenerate: "Monthly preparation",
    debits: "Payment history",
    receipts: "Receipts",
    costs: "Costs",
    summary: "Summary",
    statistics: "Statistics",
    whatsapp: "WhatsApp",
    queues: "Queues",
    templates: "Templates",
    messages: "Messages",
    users: "User",
    logout: "Log out"
  },

  form: {
    payment: {
      cash: "Cash",
      bankTransfer: "Bank transfer",
      pos: "POS",
      creditCard: "POS",
      debitCard: "POS",
      electronicPayment: "Electronic payment"
    },
    partnerType: {
      pony: "Pony",
      horse: "Horse",
      owner: "Owner",
      other: "Other"
    },
    service: {
      pony: "Pony",
      horse: "Horse",
      owner: "Owner",
      other: "Other",
      costs: {
        institutional: "Institutional costs",
        commercial: "Commercial costs",
        promiscuous: "Mixed costs",
        other: "Other costs"
      },
      acquired: {
        generic1: "Horse care",
        generic2: "Services",
        generic3: "Staff",
        generic4: "Other institutional expenses",
        generic5: "Extraordinary expenses",
        generic6: "Other"
      }
    },
    paymentFrequency: {
      mothly_fee: "Monthly",
      weekly_fee: "Weekly",
      yearly_fee: "Yearly",
      one_time_fee: "One-off",
      no_fee: "Free"
    },
    month: {
      january: "January", february: "February", march: "March", april: "April",
      may: "May", june: "June", july: "July", august: "August",
      september: "September", october: "October", november: "November", december: "December"
    },
    whatsapp: {
      none: "None",
      lastReceipt: "Last receipt",
      lastUnpaidReceipts: "Unpaid receipts",
      receiptPaid: "Paid receipt (single send)",
      receiptUnpaid: "Collection notice (single send)",
      genericAttachment: "Attachment",
      pending: "Pending",
      processing: "Processing",
      sent: "Sent",
      delivered: "Delivered",
      read: "Read",
      failed: "Failed"
    },
    streettype: {
      street: "Street", boulevard: "Boulevard", avenue: "Avenue",
      square: "Square", plaza: "Plaza", alley: "Alley",
      road: "Road", esplanade: "Esplanade"
    },
    relationship: {
      father: "Father", mother: "Mother", other: "Other",
      parent1: "Parent 1", parent2: "Parent 2", legal_guardian: "Legal guardian"
    },
    numberType: { cell: "Mobile", home: "Home", office: "Office" },
    emailType: { personal: "Personal", office: "Office" },
    documentType: {
      identity_card: "Identity card",
      electronic_identity_card: "Electronic identity card",
      passport: "Passport",
      driving_license: "Driving licence",
      residence_permit: "Residence permit",
      health_card_with_photo: "Health card with photo"
    }
  },

  home: {
    title: "Dashboard",
    currentPeriod: "Current period: {{period}}",
    activePartners: "Active members",
    activePartnersSub: "enrolled at the stable",
    creditsPeriod: "Period credits",
    creditsPeriodSub: "to collect",
    collected: "Collected",
    collectedSub: "payments received",
    balance: "Balance",
    balanceSub: "credits − debits",
    incomePeriod: "Period income",
    settledLabel: "Settled: {{count}}",
    unsettledLabel: "Outstanding: {{count}}",
    quickAccess: "Quick access",
    links: {
      partnersDesc: "Manage the stable's members",
      servicesDesc: "Services catalogue",
      creditsDesc: "Amounts to collect",
      receiptsDesc: "Receipts history",
      costsDesc: "Stable expenses",
      whatsappDesc: "Message campaigns",
      composeWaDesc: "Send WhatsApp messages to members",
      messagesWaDesc: "Sent messages history",
      queuesWaDesc: "WhatsApp send queues",
      templatesWaDesc: "Message templates",
      debitsDesc: "Payment history",
      statisticsDesc: "Reports and analytics",
      summaryDesc: "Period economic summary",
      creditsGenerateDesc: "Generate the month's receipts",
      partnerServicesDesc: "Members' recurring fees"
    }
  },

  partners: {
    title: "Members",
    subtitle: "Riding-school members — {{count}} members",
    newPartner: "New member",
    searchPlaceholder: "Search by first name, last name or tax code...",
    type: "Type",
    contact: "Contact",
    socio: "Member",
    noneFound: "No member found",
    noneFoundDesc: "Adjust the filters or add a new member.",
    addPartner: "Add member",
    deleteConfirm: "Delete member {{name}}?",
    searchFilterPlaceholder: "Search by first name, last name or tax code...",
    typeFilter: "Member type",
    allTypes: "All types",
    activeToggleAll: "All",
    activeToggleActive: "Active",
    activeToggleInactive: "Inactive",
    actions: {
      edit: "Edit",
      editDesc: "Open the sheet and update the member's details.",
      enroll: "Services and payment",
      enrollDesc: "Guided enrolment: recurring services, monthly sheet and payment.",
      credits: "Member credits",
      creditsDesc: "Go to this member's credits (amounts due).",
      payments: "Member payments",
      paymentsDesc: "Go to the payments registered for this member.",
      receipts: "Member receipts",
      receiptsDesc: "Go to this member's sheets/receipts.",
      services: "Member services",
      servicesDesc: "Manage the member's recurring subscriptions/services.",
      activate: "Activate member",
      activateDesc: "Restore the member as operational at the stable.",
      deactivate: "Deactivate member",
      deactivateDesc: "The member will no longer appear in operational selections until reactivated.",
      delete: "Delete",
      deleteDesc: "Permanently removes the record from the registry (irreversible)."
    }
  },

  enrollment: {
    title: "Enrolment — {{name}}",
    steps: { partner: "Details", services: "Recurring services", sheet: "Monthly sheet", payment: "Payment" },
    servicesIntro: "Link the member's recurring services (periodic fees). You can skip this step with Next.",
    serviceLabel: "Recurring service",
    noServices: "No recurring service linked.",
    removeServiceConfirm: "Remove service {{name}} from the member?",
    sheetIntro: "Pick the month and prepare the sheet: recurring service fees are charged automatically. Then you can edit them or add more items.",
    prepare: "Prepare sheet",
    prepared: "Sheet prepared: {{count}} items added.",
    nothingGenerated: "No new recurring fee for this month: add items manually if needed.",
    noCompetences: "No items for this month.",
    paymentIntro: "Register the payment for the {{period}} sheet.",
    nothingToPay: "Nothing to collect for this month.",
    paymentSaved: "Payment registered.",
    openReceipt: "Open sheet",
    finish: "Done"
  },

  partnerForm: {
    titleEdit: "Edit {{mode}} Member",
    titleNew: "New {{mode}} Member",
    adult: "ADULT",
    minor: "MINOR",
    min2: "At least 2 characters",
    required: "Required",
    invalidEmail: "Invalid email",
    firstName: "First name",
    lastName: "Last name",
    partnerType: "Member type",
    fiscalCode: "Tax code",
    birthCountry: "Country of birth",
    birthCountryIso: "Country of birth (ISO3)",
    birthCity: "Town of birth",
    birthCityIstat: "Town of birth (ISTAT code)",
    birthDate: "Date of birth",
    sectionResidence: "Residence",
    residenceCity: "Town of residence",
    residenceCityIstat: "Town of residence (ISTAT code)",
    cap: "Postal code",
    streetType: "Street type",
    residenceName: "Residence name",
    civic: "Street number",
    sectionDocument: "Document",
    documentType: "Document type",
    documentId: "Document ID",
    expiryDate: "Expiry date",
    releasedBy: "Released by",
    sectionLegalRep: "Legal representative",
    relationship: "Relationship",
    sectionDeduction: "Deduction",
    sectionEmail: "Email",
    addEmail: "Add email",
    emailLabel: "Email",
    emailDescription: "Email description",
    emailType: "Email type",
    sectionContacts: "Contacts",
    addContact: "Add contact",
    phoneNumber: "Phone number",
    phoneDescription: "Description",
    phoneType: "Number type",
    saveChanges: "Save changes",
    lookupError: "Address lookup unavailable. You can still enter the codes manually."
  },

  receipts: {
    title: "Receipts",
    subtitle: "Payment sheets by member and period",
    socio: "Member",
    statusPartner: "Member status",
    typePartner: "Member type",
    unpaid: "Unsettled",
    paid: "Settled",
    allReceipts: "All",
    activePartners: "Active",
    inactivePartners: "Inactive",
    allPartners: "All members",
    due: "Due",
    paidAmount: "Paid",
    settled: "Settled",
    lastPayment: "Last payment",
    none: "No receipts",
    noneDesc: "Adjust the filters to see other sheets.",
    deleteConfirm: "Delete receipt #{{number}}? The period's credits and payments will be removed too.",
    actions: {
      open: "Open sheet",
      openDesc: "Detail and payment registration",
      print: "Print receipt",
      printDesc: "Send the receipt to the printer",
      pdf: "Export PDF",
      pdfDesc: "Open the receipt as PDF",
      whatsapp: "Send via WhatsApp",
      whatsappDesc: "Send the receipt to the member on WhatsApp",
      credits: "Member credits",
      creditsDesc: "Go to this member's credits",
      debits: "Member payments",
      debitsDesc: "Go to this member's payments",
      delete: "Delete",
      deleteDesc: "Permanently delete the receipt"
    },
    wa: {
      confirmTitle: "Send receipt",
      confirmSend: "Send",
      confirm: "Send the {{period}} receipt to {{name}} via WhatsApp?",
      sent: "Receipt sent via WhatsApp",
      window: "Window closed: the request message was sent first. The receipt will go out as soon as the member replies.",
      queued: "Receipt queued: sending in progress",
      noPhone: "The member has no valid WhatsApp number"
    }
  },

  receiptDetail: {
    title: "Receipt sheet",
    titleWithNumber: "Receipt sheet No. {{number}}",
    subtitle: "{{name}} — period {{period}}",
    subtitlePeriod: "Period {{period}}",
    due: "Due",
    paid: "Paid",
    toPay: "To pay",
    headerLabel: "Bill to",
    paymentsTitle: "Payments received — {{amount}}",
    registerPayment: "Register payment",
    editPayment: "Edit payment",
    noPayments: "No payments",
    method: "Method",
    receiptNumber: "Receipt no.",
    competencesTitle: "Charges — {{amount}}",
    addCompetence: "Add competence",
    fromSubscription: "From subscription",
    noCompetences: "No charges",
    manageCompetences: "Manage charges",
    manageCompetencesDesc: "Go to the member's credits for the period",
    managePayments: "Manage payments",
    managePaymentsDesc: "Go to the member's payments for the period",
    deleteCompetence: "Delete the charge",
    paymentMethod: "Payment method",
    paymentDate: "Payment date",
    summary: "Due {{due}} · Paid {{paid}} · Remaining {{remaining}}",
    deletePaymentConfirm: "Delete the payment of {{amount}}?",
    deleteCompetenceConfirm: 'Delete the charge "{{name}}"?',
    editAction: "Edit",
    editActionDesc: "Edit the payment",
    deleteAction: "Delete",
    deletePaymentDesc: "Delete the payment"
  },

  debits: {
    title: "Payment history",
    subtitle: "Payments received from members",
    register: "Register payment",
    searchPlaceholder: "Search by member...",
    socio: "Member",
    method: "Method",
    receiptNumber: "Receipt no.",
    dateCol: "Date & time",
    none: "No payments",
    noneDesc: "Register the first payment.",
    deleteConfirm: "Delete the payment from {{name}}?",
    filteredSingle: "Filtered view for a single member{{name}}.",
    showAll: "Show all",
    filterPeriod: "Period",
    filterYear: "Year",
    filterCustom: "Custom",
    dateFrom: "From",
    dateTo: "To",
    allPartners: "All members",
    form: {
      memberRequired: "Select a member",
      amountRequired: "Amount is required",
      periodRequired: "Period is required",
      dateRequired: "Payment date is required",
      memberLabel: "Member *",
      amountLabel: "Amount (€) *",
      periodLabel: "Period (MM/YYYY) *",
      periodPlaceholder: "e.g. 01/2025",
      dateLabel: "Payment date *",
      methodLabel: "Payment method",
      submit: "Register"
    }
  },

  creditsGen: {
    title: "Monthly preparation",
    subtitle: "Generate the period's receipts and assign services to members",
    calculate: "Calculate",
    calculateConfirm: "Generate the recurring services for period {{period}}? Existing services won't be duplicated (members with multiple subscriptions of the same service still get the correct quantity).",
    calcResult: "Created {{created}} · skipped {{skipped}} · members {{affected}}",
    serviceTemplate: "Service template to assign",
    selectService: "Service",
    notifications: "Notifications",
    deductible: "Deductible",
    nameLabel: "Service name",
    priceLabel: "Price (€)",
    descriptionLabel: "Service description",
    nameError: "Invalid name",
    priceError: "Enter a non-zero amount (negative values allowed as a discount)",
    descriptionError: "Invalid description",
    templateHint: "Pick a service and fill the fields, then use ＋ next to a member to add it to their period receipt.",
    activePartners: "Members",
    searchPartner: "Search member...",
    addToPartner: "Add to member's receipt",
    typeSocio: "Member type",
    socio: "Member",
    periodCredits: "Period credits — {{period}}",
    serviceType: "Service type",
    fromSubscription: "From subscription",
    onlySelected: "Selected service only",
    openReceipt: "Open member's receipt",
    deleteCredit: "Delete credit",
    deleteConfirm: 'Delete the credit "{{name}}" of {{partner}}?',
    noPartners: "No active members",
    noCredits: "No credits for the period",
    addedToast: "Service added to {{name}}'s receipt"
  },

  credits: {
    title: "Credits",
    subtitle: "Amounts to collect from members",
    searchPlaceholder: "Search...",
    partner: "Member",
    deductible: "Deductible",
    totalFiltered: "Filtered total:",
    filteredSingle: "Filtered view for a single member.",
    showAll: "Show all",
    none: "No credits",
    noneDesc: "No credits for the selected period.",
    editTitle: "Edit competence",
    addTitle: "Add competence",
    competenceLabel: "Competence (course/extra)",
    deleteConfirm: "Delete competence \"{{name}}\"?"
  },

  costs: {
    title: "Costs",
    subtitle: "Stable expenses and outgoings",
    searchPlaceholder: "Search...",
    acquiredService: "Acquired service",
    supplier: "Supplier",
    method: "Method",
    settled: "Settled",
    totalExpenses: "Total expenses:",
    register: "New cost",
    editCost: "Edit cost",
    exportPdf: "Export year PDF",
    none: "No costs",
    noneDesc: "No cost entries recorded for the selected year.",
    deleteConfirm: 'Delete the cost "{{name}}"?',
    editAction: "Edit",
    editActionDesc: "Edit the cost details.",
    deleteAction: "Delete",
    deleteActionDesc: "Permanently removes the cost (irreversible).",
    form: {
      supplierLabel: "Supplier *",
      serviceLabel: "Acquired service *",
      priceLabel: "Amount (€) *",
      dateLabel: "Payment date *",
      methodLabel: "Payment method",
      settledLabel: "Settled",
      supplierRequired: "Supplier is required",
      serviceRequired: "Acquired service is required",
      priceRequired: "Amount is required",
      submit: "Add"
    }
  },

  servicesAcquired: {
    title: "Acquired Services",
    subtitle: "Cost items catalogue (suppliers)",
    addItem: "Add item",
    editItem: "Edit item",
    notice: "Catalogue of supplier cost items, referenced by Costs.",
    searchPlaceholder: "Search by name...",
    nameCol: "Acquired service",
    type: "Type",
    price: "Price",
    none: "No items",
    noneDesc: "Add a cost item to the catalogue.",
    emailNotification: "Email notification",
    form: {
      nameRequired: "Name is required",
      typeRequired: "Type is required",
      genericRequired: "Generic type is required",
      priceRequired: "Price is required",
      nameLabel: "Name *",
      descriptionLabel: "Description",
      typeLabel: "Cost/Service type *",
      typeHelper: "",
      genericLabel: "Economic report item *",
      genericHelper: "",
      priceLabel: "Price (€) *",
      submitAdd: "Add"
    },
    deleteConfirm: 'Delete the item "{{name}}"?'
  },

  partnerServices: {
    title: "Periodic Fees",
    subtitle: "Recurring fees assigned to active members, with per-member overrides.",
    newAssociation: "New periodic fee",
    editAssociation: "Edit association",
    associateService: "Associate service",
    socio: "Member",
    service: "Service",
    socioPlaceholder: "Filter by active member",
    servicePlaceholder: "Filter by recurring service",
    clearFilters: "Clear filters",
    noneFound: "No association found",
    noneFoundDesc: "Adjust the filters or create a new member-service association.",
    linkedServices: "{{count}} linked services",
    colDescription: "Description",
    colPrice: "Price",
    colNotifications: "Notifications",
    colDeductible: "Deductible",
    base: "Base: {{name}}",
    overrideTitle: "Overrides for the selected member",
    nameLabel: "Service name",
    descriptionLabel: "Service description",
    notifications: "Notifications",
    deductible: "Deductible",
    listPrice: "List price: {{price}}",
    noName: "Unnamed service",
    noDescription: "No description",
    loadError: "Error loading data",
    editAction: "Edit",
    editActionDesc: "Update overrides and link.",
    deleteAction: "Delete",
    deleteActionDesc: "Removes the service from the member.",
    deleteConfirm: 'Remove "{{name}}" from {{partner}}?',
    thisMember: "this member",
    form: {
      memberRequired: "Select a member",
      serviceRequired: "Select a service",
      nameRequired: "Name is required",
      descriptionRequired: "Description is required"
    }
  },

  summary: {
    title: "Financial Summary",
    subtitle: "Financial analysis by period",
    noData: "No data available",
    noDataDesc: "No summary for the period {{period}}.",
    totalCredits: "Total credits",
    toCollect: "to collect",
    collected: "Collected",
    paymentsReceived: "payments received",
    costs: "Costs",
    periodOutflows: "period outflows",
    netBalance: "Net balance",
    inMinusOut: "income − outflows",
    collectionRate: "Collection rate",
    collectedOf: "Collected {{paid}} of {{total}}",
    remaining: "Remaining: {{amount}}",
    receiptsDetail: "Receipts detail",
    settled: "Settled",
    unsettled: "Outstanding",
    totalReceipts: "Total receipts",
    balance: "Balance",
    tabDashboard: "Dashboard",
    tabDetail: "Detail by member",
    socio: "Member",
    partnerType: "Member type",
    contact: "Contact",
    total: "Total",
    colType: "Type",
    colName: "Name",
    colDescription: "Description",
    colValue: "Value",
    expandAll: "Expand all",
    closeAll: "Collapse all",
    noPartnerData: "No credits for the selected filters",
    peopleCount: "{{count}} members",
    rowsPerPage: "Members per page:"
  },

  stats: {
    title: "Statistics",
    subtitle: "Period financial analysis",
    all: "All",
    tabSoci: "Members",
    tabServizi: "Services",
    tabInsoluti: "Unpaid",
    tabSommario: "Summary",
    partnerType: "Member type",
    courses: "Courses",
    extra: "Extra",
    collected: "Collected",
    paid: "Paid",
    due: "To collect",
    units: "Units",
    amount: "Amount",
    quantity: "Quantity",
    serviceType: "Service type",
    courseExtra: "Course / Extra",
    detailTitle: "Detail by service",
    perPartnerTitle: "Detail by member",
    socio: "Member",
    contact: "Contact",
    courseName: "Course name",
    summaryByType: "Summary by member type",
    totalDue: "Total due",
    balance: "Balance",
    unsettledSheets: "Unsettled sheets",
    unsettledList: "Unsettled sheets list",
    allPeriods: "all periods",
    scope: "Period scope",
    onlyPeriod: "Only period",
    scopeYear: "Since {{year}} start",
    scopeOnly: "Only {{period}}",
    scopeUntil: "Up to {{period}}",
    periodAndPrevious: "Period + previous",
    sheetsShort: "sheets",
    sheet: "Sheet no.",
    totalSheets: "Total sheets",
    total: "Total",
    noData: "No data for the period",
    coursesCol: "Course",
    extraCol: "Extra",
    paidCol: "Collected",
    nCol: "No."
  },

  statistics: {
    title: "Statistics",
    subtitle: "Stable overview",
    totalPartners: "Total members",
    activePartners: "Active members",
    catalogServices: "Catalogue services",
    totalCredits: "Total credits",
    totalCosts: "Total costs",
    avgServicePrice: "Average service price",
    partnersByType: "Members by type",
    partnersSummary: "{{total}} total — {{active}} active / {{inactive}} inactive",
    creditsByPeriod: "Credits by period",
    last6: "Last 6 periods",
    costsByCategory: "Costs by category",
    topCategories: "Top expense categories"
  },

  suppliers: {
    title: "Suppliers",
    subtitle: "Companies and suppliers of the stable",
    newSupplier: "New supplier",
    addSupplier: "Add supplier",
    editSupplier: "Edit supplier",
    searchPlaceholder: "Search by name or VAT...",
    companyName: "Company name",
    contact: "Contact",
    vat: "VAT: {{value}}",
    none: "No suppliers",
    actionsAria: "Actions on {{name}}",
    fallbackName: "supplier",
    editAction: "Edit",
    editActionDesc: "Update company name, contacts and tax details.",
    deleteAction: "Delete",
    deleteActionDesc: "Removes the supplier from the registry.",
    deleteConfirm: 'Delete supplier "{{name}}"?',
    form: {
      nameRequired: "Name is required",
      invalidEmail: "Invalid email",
      companyLabel: "Company name *",
      vatLabel: "VAT number",
      phoneLabel: "Phone",
      emailLabel: "Email"
    }
  },

  user: {
    title: "User profile",
    subtitle: "Your account information",
    accountDetails: "Account details",
    username: "Username",
    fullName: "Full name",
    email: "Email",
    userId: "User ID"
  },

  services: {
    title: "Courses/extra",
    subtitle: "Catalogue of offered courses and extras",
    newService: "New service",
    addService: "Add service",
    searchPlaceholder: "Search service...",
    none: "No services",
    colServiceCourse: "Service / Course",
    type: "Type",
    price: "Price",
    frequency: "Frequency",
    deductible: "Deductible",
    deductibleYes: "Deductible",
    deductibleNo: "Not deductible",
    editAction: "Edit",
    editActionDesc: "Open the service/course form.",
    deleteAction: "Delete",
    deleteActionDesc: "Removes the service from the catalogue.",
    actionsAria: "Actions on {{name}}",
    editTitle: "Edit service/course: {{name}}",
    newTitle: "New service / course",
    deleteConfirm: 'Delete the service "{{name}}"?',
    form: {
      nameRequired: "Name is required",
      descriptionRequired: "Description is required",
      priceRequired: "Price is required",
      frequencyRequired: "Frequency is required",
      typeRequired: "Service type is required",
      monthsRequired: "Select at least one billing/renewal month",
      nameLabel: "Name *",
      typeLabel: "Service type *",
      deductibleQuotaLabel: "Deductible amount (€)",
      deductibleQuotaHelper: "Maximum deductible amount (0 if not applicable)",
      priceLabel: "Price (€) *",
      descriptionLabel: "Description *",
      frequencyLabel: "Renewal frequency *",
      monthsLabel: "Billing months",
      courseDeductible: "Deductible course",
      notifyDelays: "Notify delays",
      extraInReport: "Extra in report"
    }
  },

  whatsapp: {
    queueStatus: {
      pending: "Pending",
      waitingWindow: "Waiting window",
      running: "Running",
      completed: "Completed",
      sent: "Sent",
      failed: "Failed",
      nothingToSettle: "Nothing to settle"
    },
    queues: {
      title: "WhatsApp Queues",
      subtitle: "Messages queued for sending",
      searchPlaceholder: "Search recipient...",
      recipient: "Recipient",
      attachment: "Attachment",
      createdAt: "Created",
      none: "No queues",
      noneDesc: "There are no queued messages.",
      deleteConfirm: 'Delete the queue for "{{name}}"?',
      launchJob: "Launch send JOB",
      running: "Sending in progress…",
      dispatched: "JOB done: {{sent}} sent, {{failed}} failed, {{blocked}} blocked",
      alreadyRunning: "A send is already running",
      deleteAction: "Delete",
      deleteActionDesc: "Removes the item from the queue (irreversible).",
      requeueAction: "Re-queue (force)",
      requeueActionDesc: "Sets the item back to pending and forces sending now, ignoring the ~8h wait between window-opening prompts. Useful for testing.",
      requeued: "Item re-queued: {{sent}} sent",
      requeueRunning: "Send already running: the item has been re-queued",
      rowsPerPage: "Queues per page:"
    },
    messages: {
      title: "WhatsApp Messages",
      subtitle: "Log of all sent messages",
      searchPlaceholder: "Search by number or text...",
      recipient: "Recipient",
      message: "Message",
      sentAt: "Sent",
      deliveredAt: "Delivered",
      allCount: "All ({{count}})",
      deliveredSummary: "✓ {{count}} delivered",
      failedSummary: "✗ {{count}} failed",
      none: "No messages",
      noneDesc: "No WhatsApp messages recorded.",
      filterSent: "Sent",
      filterDelivered: "Delivered",
      filterRead: "Read",
      filterPending: "Pending",
      filterFailed: "Failed",
      unknownPartner: "Unknown member",
      attachment: "Attachment",
      readMore: "Read all",
      fullMessageTitle: "Full message",
      failureReason: "Failure reason",
      noFailureReason: "Reason not available",
      resend: "Resend",
      resendHint: "Resend the whole batch to this member (if the 24h window is closed, the request message goes first)",
      resendDone: "Resend started: {{sent}} messages sent",
      resendWindow: "Window closed: the request message was sent first. The resend will go out as soon as the member replies.",
      resendRunning: "A send is already running: the queue has been re-queued",
      resendNoQueue: "Original batch no longer available: cannot resend"
    },
    templates: {
      title: "WhatsApp Templates",
      subtitle: "Reusable message templates",
      newTemplate: "New template",
      editTemplate: "Edit template",
      searchPlaceholder: "Search templates...",
      name: "Name",
      text: "Text",
      attachment: "Attachment",
      none: "No templates",
      noneDesc: "Create the first template for WhatsApp messages.",
      nameRequired: "Name is required",
      messageRequired: "Message is required",
      nameLabel: "Name *",
      attachmentLabel: "Attachment",
      messageLabel: "Message text *",
      messageHelper: "Use {{variable}} to insert dynamic variables",
      submitCreate: "Create",
      deleteConfirm: 'Delete the template "{{name}}"?',
      editAction: "Edit",
      editActionDesc: "Edit the template name, message and attachment.",
      deleteAction: "Delete",
      deleteActionDesc: "Permanently removes the template (irreversible)."
    },
    compose: {
      title: "Send messages",
      subtitle: "Pick the message, select members and launch the send",
      messageLabel: "Message",
      attachmentType: "Attachment type",
      messagePreview: "Message text",
      partnerType: "Member type",
      partnerStatus: "Member status",
      searchName: "Filter member",
      available: "Available",
      selected: "To send",
      addAll: "Add all",
      removeAll: "Remove all",
      enqueue: "Queue {{count}} messages",
      launchJob: "Launch queued messages JOB",
      launchHint: "After queueing, launch the send from the WA Queues page.",
      enqueued: "{{count}} messages prepared to send",
      dispatched: "JOB launched: {{sent}} sent, {{failed}} failed, {{blocked}} blocked (test mode: {{test}})",
      noPhone: "Invalid number",
      noTemplate: "Select a message first",
      attachmentSoon: "Attachments will be available soon",
      chooseFile: "Choose attachment",
      attachmentRequired: "Upload an attachment for this message",
      active: "Active",
      inactive: "Inactive"
    }
  },

  install: {
    title: "Install the app",
    message: "Add Kappa Gestione to your Home screen to open it like an app — faster and full-screen.",
    action: "Install",
    later: "Later",
    iosHint: "Tap Share at the bottom, then \"Add to Home Screen\"."
  },

  // Human-readable translation of WhatsApp send error codes (Twilio 63xxx + Meta 13xxxx + common).
  whatsappErrors: {
    generic: "WhatsApp delivery error",
    "63001": "Channel authentication failed (Twilio/WhatsApp credentials)",
    "63002": "WhatsApp sender (From) not found",
    "63003": "Recipient not found or invalid on the WhatsApp channel",
    "63005": "Message content rejected by the channel",
    "63007": "Invalid or unconfigured WhatsApp sender",
    "63010": "Twilio channel internal error: retry",
    "63012": "WhatsApp/Meta internal error: retry",
    "63013": "Send blocked by WhatsApp policy",
    "63016": "Message outside the 24-hour window: an approved template is required",
    "63017": "The 24h conversation window is closed",
    "63018": "Rate limit exceeded (too many messages/templates): try again later",
    "63019": "Invalid request (message format)",
    "63020": "Invalid request (parameters)",
    "63021": "Message rejected by WhatsApp (invalid number or policy)",
    "63022": "Invalid template parameters",
    "63023": "Invalid or unapproved template",
    "63024": "Invalid recipient: the number isn't on WhatsApp or hasn't accepted the terms",
    "63025": "Operation not allowed on the channel",
    "63026": "Content/template not allowed",
    "63027": "Invalid WhatsApp sender number",
    "63028": "Too many requests: limit reached",
    "63029": "Message not delivered by the channel",
    "63030": "Unsupported attachment/media type",
    "63031": "Sender and recipient cannot be the same",
    "63032": "Message blocked (user blocked the sender or policy)",
    "63033": "Message not delivered",
    "63049": "Meta chose not to deliver this marketing message",
    "63051": "Per-user marketing message limit reached",
    "63052": "Invalid WhatsApp number",
    "63105": "WhatsApp sender not registered/enabled",
    "63112": "WhatsApp Business Account disabled by Meta (or verification pending)",
    "131000": "Generic WhatsApp/Meta error",
    "131026": "Undeliverable message (number not on WhatsApp or unreachable)",
    "131047": "24 hours required since the member's last reply: use a template",
    "131049": "WhatsApp throttled the send to maintain quality: try again later",
    "131051": "Unsupported message type",
    "131053": "Unable to upload the attached media",
    "470": "Message outside the 24h window: use a template",
    "30008": "Delivery failed (unknown error)",
    "21610": "The recipient unsubscribed (sent STOP)"
  }
};
